import React, {
  useCallback, useEffect, useMemo, useRef,
} from 'react';

import AutoSizer from 'react-virtualized-auto-sizer';
import InfiniteLoader from 'react-window-infinite-loader';
import { VariableSizeList } from 'react-window';

import { StyledOcticon, Timeline } from '@primer/react';
import { WorkflowIcon } from '@primer/octicons-react';

import parseISO from 'date-fns/parseISO';

import reverse from 'lodash/reverse';
import sortBy from 'lodash/sortBy';
import uniqBy from 'lodash/uniqBy';


import CollapsibleCardView from '../common/CardView/CollapsibleCardView';
import ErrorLabel from '../common/Labels/ErrorLabel';
import LoadingSpinner from '../common/LoadingSpinner/LoadingSpinner';
import ModelAssociationItem from './ModelAssociationItem';
import PipelineIcon from '../common/icons/PipelineIcon';
import VersionHistoryItem from './ModelAssociationList';
import VerticalGradientOverlay from '../common/overlays/VerticalGradientOverlay';

const sortModelAssociations = (modelAssociations = []) => modelAssociations
  ? reverse(sortBy(uniqBy(modelAssociations, 'pipeline_id'), association => parseISO(association.updated_at)))
  : modelAssociations;

const VersionHistoryItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <VersionHistoryItem {...props} />
  </div>
))

const LoadingVersionItemWithRef = React.forwardRef((props, ref) => (
  <div ref={ref}>
    <ModelAssociationItem {...props} />
  </div>
))

const ActivityRow = ({ 
  index, 
  style,
  data
}) => {
  const { 
    sortedVersionHistory,
    listRef,
    rowHeights,
    isItemLoaded,
    showCondensed
  } = data;
  const rowRef = useRef({});
  const {
    model_id,
    model_version,
    pipeline_id,
    pipeline_version,
  } = sortedVersionHistory[index];

  const setRowHeight = useCallback((rowIndex, size) => {
    listRef?.current?.resetAfterIndex(0);
    rowHeights.current = { ...rowHeights.current, [rowIndex]: size }
  }, [listRef, rowHeights]);

  useEffect(() => {
    if (rowRef.current) {
      setRowHeight(index, rowRef.current.clientHeight);
    }
  }, [index, setRowHeight]);


  if (!isItemLoaded(index)) {
    return <LoadingVersionItemWithRef ref={rowRef} condensed={showCondensed} />
  }

  return (
    <div style={style}>
      <VersionHistoryItemWithRef
        ref={rowRef}
        key={`${model_version}-${pipeline_id}`}
        entry={sortedVersionHistory[index]}
        condensed={showCondensed}
        modelID={model_id}
        pipelineID={pipeline_id}
        modelVersion={model_version}
        pipelineVersion={pipeline_version}
      />
    </div>
  )
};

const ActivityTimeline = ({ 
  width,
  sortedVersionHistory,
  listRef,
  rowHeights,
  isItemLoaded,
  showCondensed,
  itemCount,
  onLoadAvailableData,
  height,
  getRowHeight
}) => {
  if (!sortedVersionHistory?.length) {
    return null;
  }

  return (
    <InfiniteLoader
      isItemLoaded={isItemLoaded}
      itemCount={itemCount}
      loadMoreItems={onLoadAvailableData}
      minimumBatchSize={5}
      threshold={5}
    >
      {({ onItemsRendered, ref }) => (
        <VariableSizeList
          ref={(list) => {
            // Pass List ref through to InfiniteLoader
            ref(list);

            // And store a copy for yourself.
            listRef.current = list;
          }}
          height={height}
          onItemsRendered={onItemsRendered}
          itemCount={sortedVersionHistory.length}
          estimatedItemSize={106}
          itemSize={getRowHeight}
          width={width}
          itemData={{
            sortedVersionHistory,
            listRef,
            rowHeights,
            isItemLoaded,
            showCondensed
          }}
        >
          {ActivityRow}
        </VariableSizeList>
      )}
    </InfiniteLoader>
  )
};

export const ModelAssociationTimeline = ({
  dataError,
  dataLoading,
  dataAvailable,
  onLoadAvailableData,
  modelAssociations,
  timelineTitle = 'Associated Pipelines',
  height = 480,
  showCondensed = false,
}) => {
  const listRef = useRef({});
  const rowHeights = useRef({});

  const sortedVersionHistory = useMemo(() => {
    if (!modelAssociations?.length) {
      return null;
    }

    return [
      ...sortModelAssociations(modelAssociations),
    ];
  }, [modelAssociations]);

  const versionHistoryLength = sortedVersionHistory?.length || 0;

  // If there are more items to be loaded then add an extra row to hold a loading indicator.
  const itemCount = dataAvailable ? versionHistoryLength + 1 : versionHistoryLength;

  // Every row is loaded except for our loading indicator row.
  const isItemLoaded = index => !dataAvailable || index < versionHistoryLength;

  const noDataMessage = dataError
    ? 'There was an error retrieving the associated pipelines for this model.'
    : 'There are no pipelines using this model.';

  const getRowHeight = useCallback((index) => {
    const rowHeight = rowHeights?.current[index]
    return rowHeight || 106;
  }, [rowHeights]);

  if (dataLoading) {
    return <LoadingSpinner animationKey="version-log-loader" useOverlay />;
  }

  return (
    <CollapsibleCardView
      disableFullHeight
      headerText={timelineTitle}
      icon={PipelineIcon}
      fixedHeight="400px"
    >
      {!sortedVersionHistory?.length ? (
        <div className="p-4 flex-grow flex flex-col justify-center items-center">
          <StyledOcticon
            className="mb-2 text-brand"
            icon={WorkflowIcon}
            size={64}
          />
          <ErrorLabel>{noDataMessage}</ErrorLabel>
        </div>
      ) : (
        <div className="overflow-scroll px-6 flex-grow">
          {/* This zIndex override creates a stacking context, which prevents the Overlays's zIndex: 20 from appearing over root components. */}
          <Timeline sx={{ zIndex: 1 }}>
            <VerticalGradientOverlay className="top-0 left-0 right-0" fromDirection="b" />
            <AutoSizer disableHeight>
              {({ width }) => (
                <ActivityTimeline
                  width={width}
                  sortedVersionHistory={sortedVersionHistory}
                  listRef={listRef}
                  rowHeights={rowHeights}
                  isItemLoaded={isItemLoaded}
                  showCondensed={showCondensed}
                  itemCount={itemCount}
                  onLoadAvailableData={onLoadAvailableData}
                  height={height}
                  getRowHeight={getRowHeight}
                />
              )}
            </AutoSizer>
            <VerticalGradientOverlay className="bottom-0 left-0 right-0" fromDirection="t" />
          </Timeline>
        </div>
      )}
    </CollapsibleCardView>
  );
};
export default ModelAssociationTimeline;
